import React from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
require("./price.css");
var styles = {
  "price": "price-1jnQj",
  "priceInactiveListPrice": "priceInactiveListPrice-l7Nnb"
};
var defaultSymbol = '$';

/**
 * Parse the input into an optional symbol and price.
 * @param {string} input The input price as a string.
 */
export function parsePrice(_ref) {
  var {
    input,
    locale,
    currencyCode,
    showDecimals
  } = _ref;
  var [, symbol, price] = input.match(/([^\d])?(\d+[,.]?\d{0,})/) || [];
  var opts = {
    minimumFractionDigits: showDecimals ? 2 : 0
  };
  if (price) {
    if (currencyCode && locale) {
      opts.style = 'currency';
      opts.currency = currencyCode;
      opts.currencyDisplay = 'narrowSymbol';
    }
    var formattedPrice;
    // Try/Catch to support below Safari 14
    try {
      formattedPrice = (typeof price === 'string' ? Number(price) : price).toLocaleString(locale, opts);
    } catch (_unused) {
      formattedPrice = price;
    }
    return {
      price: formattedPrice,
      symbol
    };
  }
  return undefined;
}

/**
 * Converts prices into a sorted array of lowest and highest price.
 * Note: unsorted array will be returned if currency symbol is included in string.
 * @param {string|Array|Number} input The input price.
 */
function pricesToArray(input) {
  var origArr = Array.isArray(input) ? input : "".concat(input).split('-');
  try {
    // Set will enforce uniqueness to avoid duplicate values
    var sortedArr = [...new Set(origArr)].sort((a, b) => {
      return Number(a) - Number(b);
    });
    var priceArray = [sortedArr[0]];
    if (sortedArr.length > 1) {
      priceArray.push(sortedArr[sortedArr.length - 1]);
    }
    return priceArray;
  } catch (_unused2) {
    return origArr;
  }
}
function ProductDiscount(classes, prices, salePrices) {
  if (!(prices && salePrices)) return null;
  var pricesArr = pricesToArray(prices);
  var salePricesArr = pricesToArray(salePrices);

  // we need to be sure that the user selected a category
  var QTY_WHEN_USER_SELECTED_PRICE = 1;
  if (!(pricesArr.length === QTY_WHEN_USER_SELECTED_PRICE && salePricesArr.length === QTY_WHEN_USER_SELECTED_PRICE)) return null;
  var resultRounded = 100 - Math.round(salePricesArr[0] / pricesArr[0] * 100);
  if (resultRounded <= 0) {
    return null;
  }
  return /*#__PURE__*/React.createElement("span", {
    className: cs(classes.pricesDiscount)
  }, " Save ".concat(resultRounded, "%"));
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function parsePrices(_ref2) {
  var {
    /* eslint-disable react/prop-types */
    currencyCode,
    providedSymbol,
    input,
    locale,
    showDecimals,
    showCurrencyCode
    /* eslint-enable react/prop-types */
  } = _ref2;
  var arr = pricesToArray(input);
  var parsed = arr.map(p => parsePrice({
    input: "".concat(p).trim(),
    locale,
    currencyCode,
    showDecimals
  }));
  var symbol = parsed[0] && parsed[0].symbol || parsed[1] && parsed[1].symbol || providedSymbol;
  var currencyCodeDisplayed = currencyCode === 'CAD' && locale === 'fr-CA' ? 'CAN' : currencyCode;
  if (parsed[0]) {
    return showCurrencyCode === 'left' ? /*#__PURE__*/React.createElement(React.Fragment, null, currencyCode && showCurrencyCode && /*#__PURE__*/React.createElement("abbr", {
      className: "price__currency-code"
    }, currencyCodeDisplayed, "\xA0"), !currencyCode && symbol, parsed[0].price, parsed[1] && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      "aria-label": "to"
    }, "\xA0-\xA0"), !currencyCode && symbol, parsed[1].price)) : /*#__PURE__*/React.createElement(React.Fragment, null, !currencyCode && symbol, parsed[0].price, !parsed[1] && currencyCode && showCurrencyCode && /*#__PURE__*/React.createElement("abbr", {
      className: "price__currency-code"
    }, "\xA0", currencyCodeDisplayed), parsed[1] && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      "aria-label": "to"
    }, "\xA0-\xA0"), !currencyCode && symbol, parsed[1].price, currencyCode && showCurrencyCode && /*#__PURE__*/React.createElement("abbr", {
      className: "price__currency-code"
    }, "\xA0", currencyCodeDisplayed)));
  }
  return false;
}
export default function Price(_ref3) {
  var {
    classes,
    currencyCode,
    locale,
    prices,
    salePrices,
    showCurrencyCode,
    showDecimals,
    showDiscount
  } = _ref3;
  var price = React.useMemo(() => parsePrices({
    currencyCode,
    input: prices,
    locale,
    providedSymbol: defaultSymbol,
    showCurrencyCode,
    showDecimals
  }), [prices, currencyCode, locale, showDecimals, showCurrencyCode]);
  var salePrice = React.useMemo(() => salePrices ? parsePrices({
    currencyCode,
    input: salePrices,
    locale,
    providedSymbol: defaultSymbol,
    showCurrencyCode,
    showDecimals
  }) : false, [salePrices, currencyCode, locale, showDecimals, showCurrencyCode]);
  return price || salePrice ? /*#__PURE__*/React.createElement("span", {
    className: cs(classes.salePrices, styles.price, 'price')
  }, salePrice && /*#__PURE__*/React.createElement("span", {
    className: "lll-hidden-visually"
  }, "Sale Price\xA0"), /*#__PURE__*/React.createElement("span", {
    className: salePrice ? cs(classes.salePrices) : cs(classes.prices)
  }, salePrice || price), salePrice && price && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "price__spacer"
  }, "\xA0"), /*#__PURE__*/React.createElement("span", {
    className: cs(styles.priceInactiveListPrice, 'price__inactive-list-price')
  }, /*#__PURE__*/React.createElement("span", {
    className: "lll-hidden-visually"
  }, "Regular Price\xA0"), /*#__PURE__*/React.createElement("span", {
    className: cs(classes.prices)
  }, price)), showDiscount && ProductDiscount(classes, prices, salePrices))) : null;
}
export var priceType = PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.number)]);
var showCurrencyType = PropTypes.oneOfType([PropTypes.string, PropTypes.bool]);
Price.propTypes = {
  classes: PropTypes.shape({
    prices: PropTypes.string,
    pricesDiscount: PropTypes.string,
    salePrices: PropTypes.string
  }),
  currencyCode: PropTypes.string,
  prices: priceType,
  salePrices: priceType,
  locale: PropTypes.string,
  showCurrencyCode: showCurrencyType,
  showDecimals: PropTypes.bool,
  showDiscount: PropTypes.bool
};
Price.defaultProps = {
  classes: {},
  currencyCode: null,
  prices: null,
  salePrices: null,
  locale: 'en-US',
  showCurrencyCode: true,
  showDecimals: false,
  showDiscount: false
};